import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { formatCurrency, formatDate } from '../../lib/formatters';
import { Link2, X, Search, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import DistributionSummaryCard from '../cards/DistributionSummaryCard';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactLinker = ({ label, currentContact, dealId, fieldName, getAuthHeaders, onLinked }) => {
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setSearching(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const searchContacts = useCallback(async (q) => {
    if (q.length < 1) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/contacts?search=${encodeURIComponent(q)}`, { headers: getAuthHeaders() });
      const list = Array.isArray(res.data) ? res.data : res.data.contacts || [];
      setResults(list.slice(0, 8));
    } catch { setResults([]); }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    const timer = setTimeout(() => searchContacts(query), 300);
    return () => clearTimeout(timer);
  }, [query, searchContacts]);

  const handleSelect = async (contact) => {
    try {
      await axios.patch(`${API_URL}/deals/${dealId}`, { [fieldName]: contact.id }, { headers: getAuthHeaders() });
      toast.success(`${label} linked to ${contact.company_name}`);
      setSearching(false);
      setQuery('');
      onLinked();
    } catch {
      toast.error(`Failed to link ${label}`);
    }
  };

  const handleUnlink = async () => {
    try {
      await axios.patch(`${API_URL}/deals/${dealId}`, { [fieldName]: null }, { headers: getAuthHeaders() });
      toast.success(`${label} unlinked`);
      onLinked();
    } catch {
      toast.error(`Failed to unlink ${label}`);
    }
  };

  if (searching) {
    return (
      <Card ref={ref}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">{label}</CardTitle>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSearching(false)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <Input
              autoFocus
              placeholder="Search contacts..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-7 h-8 text-sm"
              data-testid={`link-${fieldName}-search`}
            />
          </div>
          <div className="max-h-40 overflow-y-auto border rounded-md">
            {loading && <p className="p-2 text-xs text-gray-400">Searching...</p>}
            {!loading && results.length === 0 && query.length > 0 && <p className="p-2 text-xs text-gray-400">No contacts found</p>}
            {results.map(c => (
              <div key={c.id} className="px-2 py-1.5 text-sm cursor-pointer hover:bg-blue-50 border-b last:border-0" onClick={() => handleSelect(c)}>
                <span className="font-medium">{c.company_name}</span>
                {c.shortcode && <span className="text-xs text-gray-400 ml-1">({c.shortcode})</span>}
                {c.name && <span className="text-xs text-gray-500 ml-1">- {c.name}</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentContact) {
    return (
      <Card className="border-dashed border-gray-300">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-500">{label}</CardTitle>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setSearching(true)} data-testid={`link-${fieldName}-btn`}>
            <Link2 className="h-3 w-3 mr-1" /> Link {label}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{label}</CardTitle>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-blue-600" onClick={() => setSearching(true)} data-testid={`change-${fieldName}-btn`}>
              <Link2 className="h-3 w-3 mr-0.5" /> Change
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-red-500" onClick={handleUnlink}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Link to={`/contacts/${currentContact.id}`} className="hover:underline">
          <p className="font-medium text-blue-700">{currentContact.company_name} <ExternalLink className="inline h-3 w-3" /></p>
        </Link>
        {currentContact.name && <p className="text-sm text-gray-700">{currentContact.name}</p>}
        <p className="text-sm text-gray-600">{currentContact.email || '-'}</p>
        <p className="text-sm text-gray-600">{currentContact.phone || '-'}</p>
      </CardContent>
    </Card>
  );
};

const SummaryTab = ({ deal, merchant, broker, funder, refinanceInfo, getAuthHeaders, onRefetch, isAdmin }) => {
  // Get refi payoff amount from refinanceInfo if available, or from deal fields
  const refiPayoffAmount = refinanceInfo?.refinanced_from?.amount_paid_off || 
                           refinanceInfo?.refi_amount || 
                           deal?.refi_balance_payoff || 
                           deal?.refinance_amount || 0;
  
  // Get deposit amount
  const depositAmount = deal?.deposit_amount || deal?.deposit || 0;
  
  // Calculate net advance (funding minus fees, refi payoff, and deposits)
  const netAdvance = (deal?.funding_amount || 0) - (deal?.fees || 0) - refiPayoffAmount - depositAmount;
  
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Deal Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-3">Original Deal Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Advance Amount:</span>
                  <span>{formatCurrency(deal?.funding_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Factor:</span>
                  <span data-testid="summary-factor">
                    {(() => {
                      const fund = parseFloat(deal?.funding_amount) || 0;
                      const pay = parseFloat(deal?.payback) || 0;
                      const computed = fund > 0 ? pay / fund : 0;
                      const stored = parseFloat(deal?.factor_rate || deal?.factor || deal?.rate) || 0;
                      const factor = stored > 0 ? stored : computed;
                      return factor > 0 ? factor.toFixed(3) : '—';
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payback Amount:</span>
                  <span>{formatCurrency(deal?.payback)}</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Actual Funding</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Funded Date:</span>
                  <span>{formatDate(deal?.funding_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Net Advance Amount:</span>
                  <span>{formatCurrency(netAdvance)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Administrative Fees:</span>
                  <span>{formatCurrency(deal?.fees || 0)}</span>
                </div>
                {depositAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Deposit Amount:</span>
                    <span className="text-amber-600">{formatCurrency(depositAmount)}</span>
                  </div>
                )}
                {refiPayoffAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Refi Payoff Amount:</span>
                    <span className="text-purple-600">{formatCurrency(refiPayoffAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Daily Collection:</span>
                  <span>{formatCurrency(deal?.payment_amount)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
            <div>
              <h3 className="font-semibold mb-3">Payment Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Net Collected:</span>
                  <span className="text-green-600 font-semibold" data-testid="summary-net-collected">{formatCurrency(deal.payments_made)}</span>
                </div>
                {(deal.gross_payments != null && deal.total_bounces != null && deal.total_bounces > 0) && (
                  <>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500 pl-3">↳ Gross Payments:</span>
                      <span className="text-gray-600" data-testid="summary-gross-payments">{formatCurrency(deal.gross_payments)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-red-600 pl-3">↳ Bounces:</span>
                      <span className="text-red-600" data-testid="summary-total-bounces">−{formatCurrency(deal.total_bounces)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Outstanding:</span>
                  <span className="text-orange-600">{formatCurrency(Math.max(0, deal.balance))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">% Repaid:</span>
                  <span>{(deal.percent_paid_thru || 0).toFixed(1)}%</span>
                </div>
                {deal.refi && (
                  <div className="flex justify-between" data-testid="summary-percent-refied-out">
                    <span className="text-gray-600">% Refied Out:</span>
                    <span className="text-purple-600 font-medium">
                      {Math.max(0, 100 - (deal.percent_paid_thru || 0)).toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Estimated Payoff Date</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Estimated:</span>
                  <span>{formatDate(deal.estimated_end_date)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Refinance Linkage Card */}
      {(refinanceInfo?.refinanced_from || refinanceInfo?.refinanced_to) && (
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-purple-800 text-sm font-semibold uppercase tracking-wide">Refinance Chain</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {refinanceInfo.refinanced_from && (
                <div className="flex items-center justify-between">
                  <span className="text-purple-700">Refinanced from:</span>
                  <Link
                    to={`/deals/${refinanceInfo.refinanced_from.id}`}
                    className="font-medium text-purple-900 hover:underline flex items-center gap-1"
                    data-testid="refi-from-link"
                  >
                    #{refinanceInfo.refinanced_from.deal_number} — {refinanceInfo.refinanced_from.deal_name}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              )}
              {refinanceInfo.refinanced_to && (
                <div className="flex items-center justify-between">
                  <span className="text-purple-700">Refinanced into:</span>
                  <Link
                    to={`/deals/${refinanceInfo.refinanced_to.id}`}
                    className="font-medium text-purple-900 hover:underline flex items-center gap-1"
                    data-testid="refi-to-link"
                  >
                    #{refinanceInfo.refinanced_to.deal_number} — {refinanceInfo.refinanced_to.deal_name}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact Cards */}
      <ContactCards merchant={merchant} broker={broker} funder={funder} dealId={deal.id} getAuthHeaders={getAuthHeaders} onRefetch={onRefetch} />

      {/* Distribution Summary (shown only when deal has syndications) */}
      <DistributionSummaryCard deal={deal} isAdmin={isAdmin} getAuthHeaders={getAuthHeaders} />
    </div>
  );
};

export const ContactCards = ({ merchant, broker, funder, dealId, getAuthHeaders, onRefetch }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <ContactLinker label="Merchant" currentContact={merchant} dealId={dealId} fieldName="company_id" getAuthHeaders={getAuthHeaders} onLinked={onRefetch} />
      <ContactLinker label="Broker/ISO" currentContact={broker} dealId={dealId} fieldName="broker_id" getAuthHeaders={getAuthHeaders} onLinked={onRefetch} />
      <ContactLinker label="Funder" currentContact={funder} dealId={dealId} fieldName="funder_id" getAuthHeaders={getAuthHeaders} onLinked={onRefetch} />
    </div>
  );
};

export default SummaryTab;
